import type { AnalysisTabs } from '@/features/analysis/types/common.types';
import type { ResizeResult } from '../types';
import { parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import {
  FileAnomalyResponse,
  FileDetection,
  FileImageResponse,
} from '@/features/analysis/types/api.types';

export function resizeCenterDoc(
  wDoc: number,
  hDoc: number,
  wStage: number,
  hStage: number,
  PADDING: number
): ResizeResult {
  const wRatio = wStage / wDoc;
  const hRatio = hStage / hDoc;

  const bestRatio = Math.min(wRatio, hRatio) - PADDING;

  const wResizedDoc = wDoc * bestRatio;
  const hResizedDoc = hDoc * bestRatio;

  const xOffset = wResizedDoc / 2;
  const yOffset = hResizedDoc / 2;

  const xPos = (wStage - wResizedDoc) / 2 + xOffset;
  const yPos = (hStage - hResizedDoc) / 2 + yOffset;

  return { wResizedDoc, hResizedDoc, bestRatio, xOffset, yOffset, xPos, yPos };
}

export function getFileMimeType(file: File): string {
  return file.type;
}

export function getDocumentType(
  file: string,
  fileName: string
): 'pdf' | 'image' {
  // Si c'est un objet File, utiliser le type MIME
  if (file.startsWith('J')) {
    return 'pdf';
  }

  if (file.startsWith('data:image/')) {
    return 'image';
  }
  // Fallback sur l'extension du fichier
  if (fileName.toLowerCase().endsWith('.pdf')) {
    return 'pdf';
  }

  return 'image';
}

export function renderImageDoc(
  toggle: AnalysisTabs,
  imageData?: FileImageResponse,
  anomalyData?: FileAnomalyResponse
): string | undefined {
  if (!imageData) return undefined;

  if (toggle === 'anomaly' && anomalyData && anomalyData.image)
    return `data:image/;base64,${anomalyData?.image}`;

  return `data:image/;base64,${imageData?.image}`;
}

export function isToday(dateString: string): boolean {
  const date = parseISO(dateString);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const today = new Date();

  return (
    today.getDate() === day &&
    today.getMonth() + 1 === month && // Months starts at 0 in JS, so we add 1
    today.getFullYear() === year
  );
}

export function translateTab(tab: AnalysisTabs): string {
  if (tab === 'anomaly') return 'Anomalie';
  if (tab === 'mrz') return 'MRZ';
  if (tab === '2ddoc') return '2D-DOC';
  return '';
}

export function downloadFile(blob: Blob, fileName: string): void {
  if (!(blob instanceof Blob)) {
    toast.error('Une erreur est survenue lors du téléchargement.');
    return;
  }
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export const getDetectionType = (
  detection: FileDetection
): AnalysisTabs | null => {
  if (
    (detection.type === 'mrz_good' || detection.type === 'mrz_bad') &&
    !!detection.mrz_chars
  ) {
    return 'mrz';
  }
  if (
    (detection.type === '2ddoc_good' || detection.type === '2ddoc_bad') &&
    !!detection['2ddoc']
  ) {
    return '2ddoc';
  }
  return null;
};

export const isDetectionClickable = (detection: FileDetection): boolean => {
  const detectionType = getDetectionType(detection);

  if (detectionType === 'mrz' && !!detection.mrz_chars) {
    return true;
  }

  if (
    detectionType === '2ddoc' &&
    detection['2ddoc'] &&
    !!detection['2ddoc'].fields
  ) {
    return true;
  }

  return false;
};

export const getDetectionOptions = (
  detection: FileDetection
): { mrz?: string; ddoc?: string } | undefined => {
  const isClickable = isDetectionClickable(detection);

  if (!isClickable) return undefined;

  const detectionType = getDetectionType(detection);

  if (detectionType === 'mrz') {
    return { mrz: detection.id };
  }

  if (detectionType === '2ddoc') {
    return { ddoc: detection.id };
  }

  return undefined;
};

export function base64ToFile(
  base64: string,
  filename: string = 'document'
): File {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([ab], { type: 'application/octet-stream' });
  return new File([blob], filename, { type: 'application/octet-stream' });
}
